/* eslint-disable react-hooks/exhaustive-deps */
"use client";

import React, { useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";

import { PasswordFormView } from "./PasswordFormView";
import { PasswordSuccessView } from "./PasswordSuccessView";

import { removeAuthCookie } from "@/services/CookiesService";

import { useApiWithLayout } from "@/hooks/api";
import { useToast } from "@/hooks/ui/useToast";

import { buildPasswordValidationRules } from "@/utils/validators/password-validation";
import { ApiErrorMessageBuilder } from "@/utils/apiErrorHandler";

import {
  API_ERROR_FALLBACKS,
  INVALID_SET_PASSWORD_LINK_CODES,
} from "@/internals/constants/errorCodes";

import postCreatePasswordHandler from "@api/postCreatePassword";

import { PostCreatePasswordPayload } from "@api/postCreatePassword/types";
import { SetPasswordProps } from "./types";

export const SetPassword = ({ setPasswordData }: SetPasswordProps) => {
  const searchParams = useSearchParams();
  const token = searchParams.get("token");

  const { toast } = useToast();

  const {
    request,
    data: createPasswordResponseData,
    error,
    loading,
  } = useApiWithLayout(postCreatePasswordHandler.postCreatePassword);

  const [password, setPassword] = useState("");
  const [successSubmit, setSuccessSubmit] = useState(null);

  const validationRules = buildPasswordValidationRules(setPasswordData, password);

  const createPasswordRequestHandler = (values: any, _methods: any) => {
    const payload: PostCreatePasswordPayload = {
      token: token as string,
      password: values.password,
      passwordConfirm: values.repeatPassword,
    };
    request(payload);
  };

  useEffect(() => {
    if (createPasswordResponseData?.status === 201 || createPasswordResponseData?.status === 200) {
      setSuccessSubmit(true);
      removeAuthCookie();
    }
  }, [createPasswordResponseData]);

  useEffect(() => {
    if (error) {
      const isInvalidLink = INVALID_SET_PASSWORD_LINK_CODES.has(error?.data?.code);
      const errorMessage = isInvalidLink
        ? setPasswordData.invalidLinkToast
        : ApiErrorMessageBuilder.buildToastMessage(error, {
            fallbackMessage: API_ERROR_FALLBACKS.AUTH.SET_PASSWORD,
          });
      toast({ message: errorMessage, type: "error", duration: 8000 });
    }
  }, [error]);

  return (
    <div
      className="recover-password"
      data-testid="Set-password"
    >
      {successSubmit === null && (
        <PasswordFormView
          title={setPasswordData.title}
          formFields={setPasswordData.formFields}
          onSubmit={createPasswordRequestHandler}
          schema={validationRules}
          isLoading={loading}
          onWatchFields={({ field, value }) => {
            if (field === "password") setPassword(value);
          }}
        />
      )}

      {successSubmit && (
        <PasswordSuccessView
          successTitle={setPasswordData.successTitle}
          successSubtitle={setPasswordData.successSubtitle}
          acceptButton={setPasswordData.acceptButton}
        />
      )}
    </div>
  );
};
